import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title, Meta } from '@angular/platform-browser';

interface HowItWorksItem {
  ID: number;
  Title: string;
  Description: string;   // HTML (<p>, <strong>)
  ImagePath: string;
  DisplayOrder: number;
  IsActive: boolean;
  Category: string;
}

@Component({
  selector: 'app-howitworks',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './howitworks.html',
  styleUrl: './howitworks.css',
})

export class Howitworks implements OnInit {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  private readonly apiBase = 'https://crm-api.redmonkwellness.com';

  //item = signal<HowItWorksItem | null>(null);
  items = signal<HowItWorksItem[]>([]);   // replace: item = signal<HowItWorksItem | null>(null);
  loading = signal(true);
  error = signal(false);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta,
  ) { }

  // ...addJsonLd() stays as it is...

  private addJsonLd(schema: object, schemaKey: string): void {
    this.document
      .querySelectorAll(`script[data-seo-schema="${schemaKey}"]`)
      .forEach((script) => script.remove());

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-schema', schemaKey);
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  // ngOnInit() {
  //     this.setSeo();
  // }

  ngOnInit(): void {
    this.setSeo();   // runs on the server too, so SEO tags are still rendered

    if (!isPlatformBrowser(this.platformId)) return;   // API call only in the browser

    this.http
      .post<{ success: boolean; Items: HowItWorksItem[] }>(
        `${this.apiBase}/Team/GetActiveAboutUs`,
        { Category: 'HowItWorks' }
      )
      .subscribe({
        next: (res) => {
          if (res.success && res.Items?.length) {
            this.items.set(res.Items);   // all items (API already returns them by DisplayOrder)
          } else if (!res.success) {
            this.error.set(true);
          }
          this.loading.set(false);
        },
        error: (err) => {
          console.error('How It Works load failed:', err);
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  imgUrl(path: string): string {
    if (!path) return '';
    return /^(https?:|data:)/i.test(path) ? path : this.apiBase + path;
  }



private setSeo(): void {

  // =========================
  // SEO DATA
  // =========================

  const pageTitle =
    'How IV Therapy Works | IV Drip Process | Red Monk Wellness';

  const description =
    'Learn how IV therapy works at Red Monk Wellness, from consultation and assessment to personalized IV drip selection and supervised treatment.';

  const canonicalUrl =
    'https://www.redmonkwellness.com/howitworks';

  const ogImage =
    'https://www.redmonkwellness.com/assets/images/services/houseof-hydration-thumb.png';

  // =========================
  // TITLE
  // =========================

  this.title.setTitle(pageTitle);

  // =========================
  // BASIC META
  // =========================

  this.meta.updateTag({
    name: 'description',
    content: description
  });

  this.meta.updateTag({
    name: 'robots',
    content:
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  });

  // =========================
  // OPEN GRAPH
  // =========================

  this.meta.updateTag({
    property: 'og:title',
    content: pageTitle
  });

  this.meta.updateTag({
    property: 'og:description',
    content: description
  });

  this.meta.updateTag({
    property: 'og:url',
    content: canonicalUrl
  });

  this.meta.updateTag({
    property: 'og:type',
    content: 'website'
  });

  this.meta.updateTag({
    property: 'og:locale',
    content: 'en_IN'
  });

  this.meta.updateTag({
    property: 'og:site_name',
    content: 'Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:image',
    content: ogImage
  });

  this.meta.updateTag({
    property: 'og:image:alt',
    content: 'How IV Therapy Works at Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:image:type',
    content: 'image/png'
  });

  // =========================
  // TWITTER / X
  // =========================

  this.meta.updateTag({
    name: 'twitter:card',
    content: 'summary_large_image'
  });

  this.meta.updateTag({
    name: 'twitter:title',
    content: pageTitle
  });

  this.meta.updateTag({
    name: 'twitter:description',
    content: description
  });

  this.meta.updateTag({
    name: 'twitter:image',
    content: ogImage
  });

  this.meta.updateTag({
    name: 'twitter:image:alt',
    content: 'How IV Therapy Works at Red Monk Wellness'
  });

  // =========================
  // GOOGLE SITE VERIFICATION
  // =========================

  this.meta.updateTag({
    name: 'google-site-verification',
    content: '7QzARYHwPFlrxb_DWbe2CdSeHLHRU-oTXeKHpi89MXg'
  });

  // =========================
  // CANONICAL URL
  // =========================

  let canonical = this.document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    this.document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);

  // =========================
  // SITEMAP
  // =========================

  let sitemap = this.document.querySelector(
    'link[rel="sitemap"]'
  ) as HTMLLinkElement | null;

  if (!sitemap) {
    sitemap = this.document.createElement('link');
    sitemap.setAttribute('rel', 'sitemap');
    sitemap.setAttribute('type', 'application/xml');
    sitemap.setAttribute('title', 'Sitemap');
    this.document.head.appendChild(sitemap);
  }

  sitemap.setAttribute(
    'href',
    'https://www.redmonkwellness.com/sitemap.xml'
  );

  // =========================
  // ORGANIZATION SCHEMA
  // =========================

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.redmonkwellness.com/#organization',
    name: 'Red Monk Wellness',
    url: 'https://www.redmonkwellness.com/',
    logo:
      'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png',
    description:
      'Red Monk Wellness offers skin, hair, aesthetic and wellness treatments through its clinics in Mumbai and Kolkata.',
    sameAs: [
      'https://www.linkedin.com/company/red-monk-wellness',
      'https://www.facebook.com/redmonkwellness'
    ]
  };

  // =========================
  // WEBPAGE SCHEMA
  // =========================

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    headline: 'How IV Therapy Works',
    description,
    isPartOf: {
      '@id': 'https://www.redmonkwellness.com/#website'
    },
    about: {
      '@type': 'Service',
      name: 'IV Therapy'
    },
    inLanguage: 'en-IN'
  };

  // =========================
  // BREADCRUMB SCHEMA
  // =========================

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.redmonkwellness.com/home'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'How It Works',
        item: canonicalUrl
      }
    ]
  };

  // =========================
  // ADD / UPDATE JSON-LD SCHEMAS
  // =========================

  const schemas = [
    {
      id: 'organization-schema',
      data: organizationSchema
    },
    {
      id: 'webpage-schema',
      data: webpageSchema
    },
    {
      id: 'breadcrumb-schema',
      data: breadcrumbSchema
    }
  ];

  schemas.forEach(({ id, data }) => {
    let script = this.document.getElementById(
      id
    ) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);
  });
}







}
